// ui/composables/useClientAuthorizations.js
import { ref, shallowRef } from 'vue';

import { createAuthorizationUnitOfWork } from '../../application/authorizationUnitOfWork';
import { commitAuthorizations } from '../../application/useCases/commitAuthorizations';
import { loadAuthorizationSession } from '../../application/useCases/loadAuthorizationSession';
import { todayDateStr } from '../../domain/services/effectiveDating';
import * as authorizationRepository from '../../infrastructure/api/authorizationApi';
import * as fundingSourceRepository from '../../infrastructure/api/fundingSourceApi';

/**
 * @typedef {Object} AuthorizationSession
 *
 * One client's authorization editing session — what `ClientAuthorizationPanel` renders.
 *
 * @property {import('vue').Ref<Object[]>} authorizations - the live working set
 * @property {import('vue').Ref<Object[]>} fundingSources - the clinic's payers, each RESOLVED to the
 *   version applying on `asOf`. Loaded here rather than passed in: anything reading them without
 *   this session's as-of would disagree with it about which payer applies.
 * @property {import('vue').Ref<boolean>} hasPendingChanges
 * @property {import('vue').Ref<boolean>} isLoading
 * @property {import('vue').Ref<boolean>} isLoaded
 * @property {string} asOf - the session's single clock read; statuses resolve against it
 * @property {(scope: { clientId: string|null, clinicId: string }) => Promise<void>} load
 * @property {(attrs: Object) => Object} add - from the form's flat attributes
 * @property {(attrs: Object) => Array<Object>} validate - the domain's verdict on a draft
 * @property {(authorizationId: string, attrs: Object) => Object} update - restates a recorded one
 * @property {(authorizationId: string) => void} remove
 * @property {(scope: { clientId: string, clinicId: string }) => Promise<void>} commit
 */

/** The methods an AuthorizationSession must expose — used to validate one at a component boundary. */
export const AUTHORIZATION_SESSION_METHODS = [
  'load', 'add', 'validate', 'update', 'remove', 'commit',
];

/**
 * Creates an authorization editing session: a unit of work over a client's authorizations, plus
 * load and flush.
 *
 * THIS SLICE'S COMPOSITION ROOT — the only place binding the Firestore adapter to the repository
 * port.
 *
 * The unit of work stays a plain, non-reactive object: `reactive()` would put Vue proxies inside the
 * domain objects handed to the API layer on flush. Every mutation republishes a snapshot into refs.
 *
 * Callers own the session's lifetime — in the client dialog that is the DIALOG rather than the tab,
 * because tab panels unmount on switch and staged edits have to outlive that.
 *
 * @returns {AuthorizationSession}
 */
export function useClientAuthorizations() {
  /**
   * The one clock read in this session, so a row cannot change status mid-edit and every status on
   * screen is judged against the same day. Every rule beneath takes the date as an argument.
   */
  const asOf = todayDateStr();

  const unitOfWork = shallowRef(createAuthorizationUnitOfWork({
    newId: authorizationRepository.newAuthorizationId,
    newLimitId: authorizationRepository.newLimitId,
  }));

  /**
   * The read in progress, if any. `isLoaded` only flips once a read RESOLVES, so it cannot dedupe
   * concurrent callers on its own — and there are some: the panel loads from an `immediate` watcher
   * inside a tab panel that unmounts on switch, so leaving and returning before the first read lands
   * starts a second against a set the user may already be editing.
   */
  let inFlight = null;

  /**
   * The flush in progress, if any. Saving the client dialog is one button, but nothing stops it
   * being pressed twice — and a second flush would re-send the same staged creates, whose ids are
   * already taken, so the transaction fails and the biller is told their save broke when it did not.
   */
  let inFlightCommit = null;

  const authorizations = ref([]);
  const fundingSources = ref([]);
  const hasPendingChanges = ref(false);
  const isLoading = ref(false);
  const isLoaded = ref(false);

  /** Republishes the unit of work's state for the templates. */
  const sync = () => {
    authorizations.value = unitOfWork.value.all();
    hasPendingChanges.value = unitOfWork.value.hasPendingChanges();
  };

  /**
   * Loads the session. Safe on every tab visit and safe concurrently — it reads once and keeps
   * whatever is already staged.
   *
   * THE `isLoaded` GUARD IS WHAT THE PANEL DEPENDS ON: the tab unmounts on switch, so its watcher
   * fires again on every visit, and without it the biller sees a spinner and a fresh read each time.
   *
   * A client that does not exist yet has none, which is not a failure: the session is usable
   * immediately so a letter can be recorded during client creation.
   *
   * @param {{ clientId: string|null, clinicId: string }} scope
   */
  async function load({ clientId, clinicId }) {
    if (isLoaded.value) {
      return undefined;
    }
    // A session belongs to one client for its whole life, so a concurrent caller wants the read
    // already running, not another one.
    if (inFlight !== null) {
      return inFlight;
    }

    isLoading.value = true;
    inFlight = (async () => {
      try {
        const session = await loadAuthorizationSession(
          { clientId: clientId ?? null, clinicId, asOf },
          { authorizationRepository, fundingSourceRepository },
        );

        fundingSources.value = session.fundingSources;
        unitOfWork.value.load(session.authorizations);
        isLoaded.value = true;
        sync();
      } finally {
        // Cleared either way: a failed read leaves `isLoaded` false, so reopening the tab retries.
        isLoading.value = false;
        inFlight = null;
      }
    })();

    return inFlight;
  } // load()

  return {
    authorizations,
    fundingSources,
    hasPendingChanges,
    isLoading,
    isLoaded,
    asOf,
    load,

    validate(attrs) {
      return unitOfWork.value.validate(attrs);
    },

    add(attrs) {
      const authorization = unitOfWork.value.add(attrs);
      sync();
      return authorization;
    },

    update(authorizationId, attrs) {
      const authorization = unitOfWork.value.update(authorizationId, attrs);
      sync();
      return authorization;
    },

    remove(authorizationId) {
      unitOfWork.value.remove(authorizationId);
      sync();
    },

    async commit({ clientId, clinicId }) {
      if (inFlightCommit !== null) {
        return inFlightCommit;
      }

      inFlightCommit = (async () => {
        try {
          const scope = { clientId, clinicId };

          await commitAuthorizations(unitOfWork.value, scope, authorizationRepository);
          sync();
        } finally {
          // Cleared either way, so a failed save can be retried with the staged work intact.
          inFlightCommit = null;
        }
      })();

      return inFlightCommit;
    },
  };
} // useClientAuthorizations()
